import styled from "styled-components";
import { motion } from "framer-motion";
import { device } from "./responsive";

const contentWidth = "min(100%, 1200px)";

export const Container = styled.section`
  position: relative;
  isolation: isolate;
  min-height: clamp(690px, 92vh, 930px);
  padding: 22px clamp(16px, 4vw, 52px) clamp(52px, 7vw, 90px);
  background:
    linear-gradient(
      98deg,
      rgba(7, 7, 7, 0.94) 0%,
      rgba(7, 7, 7, 0.9) 34%,
      rgba(7, 7, 7, 0.44) 60%,
      rgba(7, 7, 7, 0.1) 100%
    ),
    url("/images/background-home.webp") 76% center / cover no-repeat;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: url("/images/background-fogo-tres-exposure.webp") center / cover
      no-repeat;
    opacity: 0.33;
    mix-blend-mode: screen;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
      radial-gradient(
        1200px 420px at 74% 100%,
        rgba(255, 94, 47, 0.2) 0%,
        rgba(255, 94, 47, 0) 72%
      ),
      radial-gradient(
        700px 240px at 14% 0%,
        rgba(255, 134, 78, 0.18) 0%,
        rgba(255, 134, 78, 0) 72%
      );
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media ${device.mobile} {
    min-height: auto;
    padding: 16px 12px 36px;
    background-position: 73% 44%;
  }
`;

export const Header = styled.header`
  width: ${contentWidth};
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 6px 0;

  @media ${device.tablet} {
    gap: 14px;
  }

  @media ${device.mobile} {
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }
`;

export const DivLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media ${device.mobile} {
    width: auto;
    gap: 8px;
    justify-content: flex-start;
  }
`;

export const Logo = styled.div`
  width: clamp(70px, 10vw, 92px);
  height: clamp(70px, 10vw, 92px);
  background: url("/images/company/logo-extincenter-sem-fundo-completed.webp")
    center / contain no-repeat;

  @media ${device.mobile} {
    width: 50px;
    height: 50px;
  }
`;

export const ContainerTextLogo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media ${device.mobile} {
    text-align: left;
    align-items: flex-start;
  }
`;

export const TitleLogo = styled.div`
  color: var(--text-primary);
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.05em;

  @media ${device.mobile} {
    font-size: clamp(1.28rem, 7.8vw, 1.74rem);
    letter-spacing: 0.035em;
  }
`;

export const SubTitleLogo = styled.div`
  color: #d7d7d7;
  font-size: clamp(0.7rem, 0.95vw, 0.84rem);
  line-height: 1.3;
  letter-spacing: 0.055em;
  text-transform: lowercase;

  @media ${device.mobile} {
    letter-spacing: 0.025em;
    font-size: 0.72rem;
    line-height: 1.25;
  }
`;

export const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  margin-left: auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: linear-gradient(
      145deg,
      rgba(255, 126, 83, 0.18) 0%,
      rgba(255, 126, 83, 0.06) 42%
    ),
    rgba(0, 0, 0, 0.48);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  padding: 0;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;

  span {
    width: 20px;
    height: 2px;
    border-radius: 999px;
    background: #f6f6f6;
    transform-origin: center;
    transition: transform 0.24s ease, opacity 0.2s ease;
  }

  span:nth-child(1) {
    transform: ${({ $isOpen }) =>
      $isOpen ? "translateY(7px) rotate(45deg)" : "translateY(0) rotate(0)"};
  }

  span:nth-child(2) {
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
  }

  span:nth-child(3) {
    transform: ${({ $isOpen }) =>
      $isOpen ? "translateY(-7px) rotate(-45deg)" : "translateY(0) rotate(0)"};
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 160, 120, 0.75);
    outline-offset: 3px;
  }

  @media ${device.mobile} {
    display: inline-flex;
  }
`;

type NavigationProps = {
  $isOpen?: boolean;
};

export const Navigation = styled.nav<NavigationProps>`
  display: flex;
  align-items: center;
  gap: clamp(8px, 1vw, 16px);
  padding: 8px 12px;
  border-radius: 999px;
  margin-left: auto;
  background: linear-gradient(
      135deg,
      rgba(255, 126, 83, 0.16) 0%,
      rgba(255, 126, 83, 0.02) 36%
    ),
    rgba(0, 0, 0, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.24),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);

  a {
    position: relative;
    color: var(--text-primary);
    text-decoration: none;
    font-size: 0.92rem;
    line-height: 1;
    font-weight: 600;
    letter-spacing: 0.02em;
    opacity: 0.92;
    padding: 8px 11px;
    border-radius: 999px;
    white-space: nowrap;

    &::after {
      content: "";
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 6px;
      height: 2px;
      border-radius: 4px;
      background: linear-gradient(90deg, #ff5d4d 0%, #d08a3c 100%);
      transform: scaleX(0);
      transform-origin: left center;
      transition: transform 0.24s ease;
    }

    &:hover,
    &:focus-visible {
      color: #fff;
      opacity: 1;
      background: rgba(255, 255, 255, 0.14);
    }

    &:hover::after,
    &:focus-visible::after {
      transform: scaleX(1);
    }
  }

  @media ${device.tablet} {
    width: auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    border-radius: 18px;
  }

  @media ${device.mobile} {
    order: 3;
    width: 100%;
    margin-left: 0;
    margin-top: ${({ $isOpen }) => ($isOpen ? "6px" : "0")};
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    overflow: hidden;
    pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
    border-radius: 14px;
    max-height: ${({ $isOpen }) => ($isOpen ? "340px" : "0")};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    transform: translateY(${({ $isOpen }) => ($isOpen ? "0" : "-8px")});
    padding: ${({ $isOpen }) => ($isOpen ? "10px" : "0 10px")};
    border-color: ${({ $isOpen }) =>
      $isOpen ? "rgba(255, 255, 255, 0.24)" : "rgba(255, 255, 255, 0.08)"};
    backdrop-filter: blur(8px);
    transition: max-height 0.28s ease, opacity 0.24s ease,
      transform 0.24s ease, padding 0.24s ease, border-color 0.24s ease,
      margin-top 0.24s ease;

    a {
      width: 100%;
      font-size: 0.92rem;
      line-height: 1.2;
      padding: 10px 12px;
      border-radius: 10px;

      &::after {
        display: none;
      }
    }
  }
`;

export const Main = styled.main`
  width: ${contentWidth};
  margin: clamp(34px, 7vw, 98px) auto 0;
  display: flex;
  align-items: flex-start;

  @media ${device.mobile} {
    margin-top: 14px;
    justify-content: center;
  }
`;

export const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  max-width: 620px;

  @media ${device.mobile} {
    align-items: center;
    text-align: center;
    max-width: min(100%, 360px);
    gap: 15px;
    margin-inline: auto;
  }
`;

export const FirstTitle = styled.span`
  display: inline-flex;
  align-items: center;
  color: #ffe8dd;
  background: linear-gradient(90deg, #962417 0%, #b9392b 100%);
  border-radius: 999px;
  padding: 7px 14px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.72rem;
  font-weight: 700;
  box-shadow: 0 8px 18px rgba(150, 36, 23, 0.4);

  @media ${device.mobile} {
    line-height: 1.4;
    letter-spacing: 0.045em;
    font-size: 0.66rem;
    padding: 6px 10px;
  }
`;

export const SecondTitle = styled.h1`
  color: var(--text-primary);
  font-size: clamp(2.95rem, 6vw, 5.35rem);
  line-height: 0.97;
  font-weight: 800;
  width: fit-content;
  max-width: 100%;
  text-shadow: 0 8px 20px rgba(0, 0, 0, 0.38);

  span {
    display: block;
    white-space: nowrap;
  }

  span + span {
    margin-top: 10px;
  }

  @media ${device.tablet} {
    span {
      white-space: normal;
    }
  }

  @media ${device.mobile} {
    font-size: clamp(2.18rem, 11.4vw, 3.16rem);
    line-height: 1.02;
    max-width: 100%;

    span {
      white-space: nowrap;
      margin-inline: auto;
    }

    span + span {
      margin-top: 6px;
    }
  }
`;

export const Subtitle = styled.p`
  color: rgba(245, 245, 245, 0.95);
  font-size: clamp(1rem, 1.25vw, 1.1rem);
  line-height: 1.72;
  max-width: 46ch;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  @media ${device.mobile} {
    max-width: 31ch;
    font-size: 0.98rem;
    line-height: 1.62;
    margin-inline: auto;
    text-align: center;
  }
`;

export const DivButton = styled.div`
  width: min(100%, 336px);
  margin-top: 4px;

  @media ${device.mobile} {
    width: min(100%, 320px);
    margin-inline: auto;
  }
`;

export const HeroUrgent = styled.p`
  color: #c7c7c7;
  font-size: 0.9rem;
  line-height: 1.6;

  a {
    color: #ff6b5f;
    text-decoration: none;
    font-weight: 700;

    &:hover {
      color: #ff8177;
    }
  }

  @media ${device.mobile} {
    text-align: center;
  }
`;

export const HeroMedia = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  min-height: clamp(360px, 45vw, 520px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 20px 46px rgba(0, 0, 0, 0.35);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 30%,
      rgba(0, 0, 0, 0.24) 74%,
      rgba(0, 0, 0, 0.6) 100%
    );
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media ${device.tablet} {
    min-height: 360px;
  }

  @media ${device.mobile} {
    min-height: 290px;
  }
`;

export const DivIcons = styled(motion.div)`
  width: ${contentWidth};
  margin: clamp(44px, 6vw, 68px) auto 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media ${device.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media ${device.mobile} {
    margin-top: 24px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

export const SectionAbout = styled(motion.section)`
  width: ${contentWidth};
  margin: 0 auto;
  padding: clamp(32px, 7vw, 92px) clamp(16px, 4vw, 46px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: clamp(18px, 3vw, 30px);
  background: linear-gradient(
      145deg,
      rgba(23, 10, 8, 0.92) 8%,
      rgba(17, 14, 12, 0.9) 52%,
      rgba(10, 10, 10, 0.94) 100%
    ),
    url("/images/background-fogo-tres-exposure.webp") center / cover no-repeat;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  overflow: hidden;

  img {
    width: 100%;
    max-width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  @media ${device.laptop} {
    display: grid;
    grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1fr);
    align-items: center;
    gap: clamp(24px, 4vw, 44px);

    img {
      max-width: 520px;
      aspect-ratio: 4 / 3;
    }
  }

  @media ${device.mobile} {
    gap: 16px;
    padding: 28px 14px;

    img {
      border-radius: 12px;
    }
  }
`;

export const ContainerTextAbout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;

  .titleAbout {
    color: #ff8f46;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-size: 0.85rem;
    font-weight: 700;
  }

  h1 {
    color: var(--text-primary);
    font-size: clamp(2rem, 4vw, 2.8rem);
    line-height: 1.22;
    font-weight: 800;
    max-width: 22ch;
  }

  p {
    color: #dbdbdb;
    max-width: 65ch;
    font-size: clamp(1rem, 1.3vw, 1.05rem);
    line-height: 1.76;
  }

  @media ${device.tablet} {
    h1 {
      max-width: 100%;
    }
  }

  @media ${device.mobile} {
    align-items: flex-start;
    text-align: left;

    h1 {
      font-size: clamp(1.55rem, 8vw, 2rem);
      line-height: 1.25;
    }

    p {
      max-width: 100%;
      font-size: 0.98rem;
      line-height: 1.7;
    }
  }
`;

export const ContainerGallery = styled(motion.section)`
  width: 100%;
  padding: clamp(56px, 7vw, 88px) clamp(20px, 4vw, 56px);
  background: transparent;
`;

export const SectionCallToAction = styled.section`
  width: ${contentWidth};
  margin: 0 auto;
  padding: clamp(46px, 6vw, 68px) clamp(20px, 4vw, 40px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 470px);
  gap: clamp(18px, 4vw, 40px);
  align-items: center;
  background:
    linear-gradient(
      140deg,
      rgba(12, 12, 12, 0.95) 0%,
      rgba(21, 21, 21, 0.88) 48%,
      rgba(12, 12, 12, 0.94) 100%
    ),
    url("/images/background-fogo-tres-exposure.webp") center / cover no-repeat;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const CallToActionContent = styled.div`
  h2 {
    color: var(--text-primary);
    font-size: clamp(2rem, 4vw, 3.1rem);
    line-height: 1.2;
    font-weight: 800;
    margin-bottom: 12px;
    max-width: 18ch;
  }

  p {
    color: #d8d8d8;
    font-size: clamp(1rem, 1.4vw, 1.2rem);
    line-height: 1.7;
    margin-bottom: 20px;
  }
`;

export const CallToActionActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  a {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: var(--text-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.08);

    &:hover {
      border-color: rgba(255, 255, 255, 0.36);
      transform: translateY(-2px);
    }
  }
`;

export const CallToActionImage = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.34);

  img {
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
  }
`;

export const SectionFooter = styled.footer`
  width: ${contentWidth};
  margin: clamp(24px, 3vw, 34px) auto 0;
  padding: clamp(42px, 5.5vw, 58px) clamp(20px, 4vw, 40px) 24px;
  background:
    linear-gradient(
      130deg,
      rgba(20, 10, 8, 0.92) 0%,
      rgba(18, 14, 12, 0.88) 50%,
      rgba(12, 12, 12, 0.94) 100%
    ),
    url("/images/background-fogo-tres-exposure.webp") center / cover no-repeat;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;

  @media ${device.mobile} {
    margin-top: 20px;
    padding: 24px 12px 18px;
    border-radius: 14px;
  }
`;

export const ContainerLogoFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);

  @media ${device.mobile} {
    justify-content: flex-start;
    text-align: left;
  }
`;

export const DivLogoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LogoFooter = styled.div`
  width: 62px;
  height: 62px;
  background: url("/images/company/logo-extincenter-sem-fundo-completed.webp")
    center / contain no-repeat;
`;

export const TitleLogoFooter = styled.div`
  color: var(--text-primary);
  font-size: clamp(1.2rem, 2.2vw, 1.7rem);
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: 0.08em;
`;

export const SubTitleLogoFooter = styled.div`
  color: #d7d7d7;
  font-size: clamp(0.72rem, 1vw, 0.82rem);
  line-height: 1.45;
  text-transform: lowercase;
  letter-spacing: 0.06em;
`;

export const InfoCompany = styled.div`
  width: 100%;
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const ContainerContactCompany = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.22);

  strong {
    color: var(--text-primary);
    font-size: 0.95rem;
    font-weight: 700;
  }

  span {
    color: #dfdfdf;
    font-size: 0.95rem;
    line-height: 1.7;
  }

  a {
    color: var(--text-primary);
    text-decoration: none;

    &:hover {
      color: #ff8c7c;
    }
  }

  @media ${device.mobile} {
    padding: 14px;
    gap: 8px;

    span {
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }
`;

export const TitleInfoCompany = styled.div`
  color: #ff5645;
  font-size: clamp(1.28rem, 2vw, 1.5rem);
  line-height: 1.2;
  font-weight: 800;
`;

export const ContactCompany = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #dfdfdf;
  font-size: 0.95rem;
  line-height: 1.7;

  svg {
    color: #ff5645;
    flex-shrink: 0;
  }

  @media ${device.mobile} {
    font-size: 0.9rem;
    gap: 6px;
  }
`;

export const CopyRight = styled.div`
  width: 100%;
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;

  span {
    color: #d4d4d4;
    font-size: 0.84rem;
    line-height: 1.6;
  }
`;

export const DivSocial = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  a {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: var(--text-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.08);

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(255, 255, 255, 0.35);
    }
  }
`;

export const FooterMapCard = styled.div`
  width: 100%;
  margin-top: 10px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.36);

  iframe {
    width: 100%;
    height: 250px;
    border: 0;
    display: block;
    filter: saturate(0.75) contrast(1.05);
  }

  @media ${device.mobile} {
    iframe {
      height: 185px;
    }
  }
`;
